import { Box, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { TenantRequestDto, TenantRequestGetPaginatedDto } from "@/core/api/generated/v1";

import TenantInline from "../Tenant/TenantInline";

export type TenantRequestAutocompleteOption = BaseAutocompleteOption<TenantRequestDto>;

// NB: it was request from customers to prioritize externalNumber in display
export const getTenantRequestNumberTitle = (data: TenantRequestDto): string =>
  StringHelper.join([data.externalNumber, data.globalNumber], " / ", {
    skipEmpty: true,
  });

export const getTenantRequestOptionTitle = (data: TenantRequestDto): string =>
  StringHelper.join(
    [
      getTenantRequestNumberTitle(data),
      data.name,
      enumService.getEnumValueName("TenantRequestType", data.content?.type),
      data.content?.operation?.operationType &&
        enumService.getEnumValueName(
          "TenantRequestOperationType",
          data.content?.operation?.operationType,
        ),
    ],
    ", ",
    {
      skipEmpty: true,
    },
  );

export const tenantRequestToOption = (data: TenantRequestDto): TenantRequestAutocompleteOption => ({
  id: data.id!,
  title: getTenantRequestOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface TenantRequestAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<TenantRequestDto> {
  searchFilters?: Partial<Omit<TenantRequestGetPaginatedDto, "search" | "includeIds">>;
}

export default function TenantRequestAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: TenantRequestAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={tenantRequestToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          tenantRequestGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          tenantRequestGetPaginatedDto: {
            ...params.tenantRequestGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Company request'
      placeholder='Search...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='tenantRequest' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                </>
              }
              secondary={
                option.data && (
                  <>
                    <Box>
                      <Typography component='div' variant='body2'>
                        {option.data?.description}
                      </Typography>
                    </Box>

                    <Stack>
                      <TenantInline entity={option.data?.senderTenant} />
                      <TenantInline entity={option.data?.receiverTenant} />
                    </Stack>
                  </>
                )
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
